import { Component, inject, signal, computed, input } from "@angular/core";
import { CursorSelectionModal } from "@components/cursor-selection-modal/cursor-selection-modal";
import { RewardCursorProgress } from "@components/reward-cursor-progress/reward-cursor-progress";
import { RewardMechanismTouchDeviceNotice } from "@components/reward-mechanism-touch-device-notice/reward-mechanism-touch-device-notice";
import { EducationLevelGroup } from "@domain/EducationLevel";
import { RewardCursorProgression as RewardCursorProgressionEntity } from "@domain/RewardCursors";
import { RdxDialogService } from "@radix-ng/primitives/dialog";
import { DeviceCapabilityService } from "@services/device-capability.service";
import { AuthState } from "@states/auth.state";

@Component({
  selector: "app-reward-cursors-progression",
  template: `
    @let currentUserHasUnlockedCursors = !!authState.currentUser()?.rewardCursors?.hasUnlockedCursors();

    <div class="reward-cursor-progression tab-content">
      <h2 class="title-h4 reward-cursor-progression__title">Ta collection de curseurs</h2>
      <p class="body-3 reward-cursor-progression__description">Encore quelques exercices pour débloquer le prochain curseur.</p>

      <div class="reward-cursor-progression__content">
        <ul class="reward-cursor-progression__cursors-list">
          @for (rewardCursorProgression of rewardCursorProgressionOrder(); track rewardCursorProgression.cursor) {
            <li class="cursors-list__item">
              @if (rewardCursorProgression.isUnlocked) {
                <app-reward-cursor-progress [rewardCursor]="rewardCursorProgression.cursor" [currentStep]="24" [isNext]="false" [isLocked]="false" />
              } @else if (rewardCursorProgression.isNext) {
                <app-reward-cursor-progress
                  [rewardCursor]="rewardCursorProgression.cursor"
                  [currentStep]="authState.currentUser()?.nextRewardCursorCorrectAnswerCount ?? 0"
                  [isNext]="true"
                  [isLocked]="false"
                />
              } @else {
                <app-reward-cursor-progress [rewardCursor]="rewardCursorProgression.cursor" [currentStep]="0" [isNext]="false" [isLocked]="true" />
              }
            </li>
          }
        </ul>
      </div>

      @if (isRewardMechanismEnabled() && currentUserHasUnlockedCursors) {
        <div class="reward-cursor-progression__actions">
          <button
            type="button"
            class="reward-cursor-progression__action"
            data-testid="reward-cursors-progression-select-cursor-button"
            (click)="handleOpenCursorSelectionModal()"
          >
            Modifier mon curseur
          </button>
        </div>
      }

      <app-reward-mecanism-touch-device-notice />
    </div>
  `,
  styles: `
    @use "responsive" as *;

    .reward-cursor-progression.tab-content {
      padding: 24px 16px 0 16px;
      display: flex;
      flex-direction: column;
      height: calc(100vh - 350px);

      @include screenWidth("from-tablet") {
        max-height: none;
      }

      @include screenWidth("from-desktop") {
        padding-left: 24px;
        padding-right: 24px;
      }

      .reward-cursor-progression__content {
        flex: 1;
        overflow-y: auto;
        min-height: 0;
        padding-bottom: 24px;

        /* WebKit-based browsers (Chrome, Edge, Safari, etc.) */
        &::-webkit-scrollbar {
          width: 8px;
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          background: #e4e0d8;
          border-radius: 100px;
        }

        &::-webkit-scrollbar-thumb:hover {
          background: #d1cdc5;
        }

        /* Firefox */
        scrollbar-width: thin;
        scrollbar-color: #e4e0d8 transparent;
      }
    }

    .reward-cursor-progression__title {
      text-align: center;
      margin-bottom: 8px;
    }

    .reward-cursor-progression__description {
      text-align: center;
      margin-bottom: 20px;
    }

    .reward-cursor-progression__actions {
      text-align: center;
      padding-bottom: 24px;

      @include screenWidth("from-desktop") {
        padding-bottom: 16px;
      }
    }

    .reward-cursor-progression__cursors-list {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      gap: 16px;
      list-style-type: none;
      padding: 0;

      @include screenWidth("from-tablet") {
        padding-left: 30px;
        padding-right: 30px;
      }
    }

    .cursors-list__item {
      width: 100px;
      height: 100px;

      @include screenWidth("from-tablet") {
        width: 120px;
        height: 120px;
      }

      &.is-unavailable {
        filter: grayscale(1);
        opacity: 0.4;
      }

      &.is-selected {
        border-color: #05bf8e;
      }
    }

    .item__image {
      display: block;
    }

    .reward-cursor-progression__action {
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 120%;
      margin-top: 16px;
      border: none;
      background: none;
      text-decoration: underline;
      cursor: pointer;
      opacity: 1;
      transition: opacity 1s ease;

      &:hover {
        opacity: 0.5;
        transition: opacity 1s ease;
      }
    }
  `,
  imports: [RewardMechanismTouchDeviceNotice, RewardCursorProgress],
})
export class RewardCursorsProgression {
  #modal = inject(RdxDialogService);
  protected deviceCapabilityService = inject(DeviceCapabilityService);
  protected authState = inject(AuthState);

  educationLevelGroup = input.required<EducationLevelGroup>();

  protected rewardCursorProgressionOrder = computed<RewardCursorProgressionEntity[]>(() => {
    const currentUser = this.authState.currentUser();
    if (!currentUser) return [];

    return currentUser.rewardCursors.getCursorProgressionWithStatus();
  });

  isRewardMechanismEnabled = signal(!this.deviceCapabilityService.hasTouchCapabilities());

  handleOpenCursorSelectionModal() {
    this.#modal.open({
      content: CursorSelectionModal,
      data: { selectedEducationLevelGroup: this.educationLevelGroup() },
    });
  }
}
